// src/features/editFeatureAggregation.js
// Aggregates per-pin features into one edit-level summary (palette + garment counts)

import { getEditFeaturesMap } from "../state/editFeaturesStore";
import { computePinSourceHash } from "../state/editFeaturesStore";
import { computeEditAggregateHash, getEditAggregate, setEditAggregate } from "../state/editAggregateStore";
import { ensurePinFeaturesAndEmbedding } from "./pinFeaturePipeline";
import { summarisePinFeatures } from "./pinFeatureSummary";

// Merge color families with weights
function countMap() {
  return Object.create(null);
}

function inc(map, key, by = 1) {
  if (!key) return;
  const k = String(key).trim().toLowerCase();
  if (!k) return;
  map[k] = (map[k] || 0) + by;
}

function incMany(map, arr, by = 1) {
  if (!Array.isArray(arr)) return;
  for (const v of arr) inc(map, v, by);
}

function asArray(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string") return v.split(/[,|]/).map(s => s.trim()).filter(Boolean);
  return [];
}

// Very lightweight intent phrase extraction from notes.
// (We can later swap this to OpenAI intent extraction without changing aggregation shape.)
function extractDesirePhrases(text) {
  const t = String(text || "").trim();
  if (!t) return [];

  const lines = t
    .split(/\n|\.|\?|!/g)
    .map(s => s.trim())
    .filter(Boolean);

  const out = [];
  for (const s of lines) {
    // capture common desire patterns
    // e.g. "looking for a longline coat", "want something tailored", "need a dress for a wedding"
    if (/\b(look(?:ing)? for|want|need|after|search(?:ing)? for|hoping for|prefer|love)\b/i.test(s)) {
      out.push(s);
      continue;
    }
    // also keep short “requirements” lines (bullet style)
    if (s.length <= 90 && /,|;|\/| - /.test(s)) out.push(s);
  }

  // de-dupe (case-insensitive)
  const seen = new Set();
  return out.filter(p => {
    const k = p.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export async function ensureEditAggregateFeatures(editId, editItems) {
  if (!editId) return null;

  const pins = Array.isArray(editItems) ? editItems : [];
  const nextHash = computeEditAggregateHash(pins, computePinSourceHash);
  const cached = getEditAggregate(editId);

  if (cached?.sourceHash === nextHash) return cached;

  // Ensure all eligible pins have features cached
  for (const pin of pins) {
    const k = String(pin?.kind || "").toLowerCase();
    if (k === "image" || k === "note") {
      await ensurePinFeaturesAndEmbedding(editId, pin);
    }
  }

  // Now aggregate from store
  const map = getEditFeaturesMap(editId); // { pinId: { kind, features, ... } }

  const garmentCounts = countMap();
  const colorCounts = countMap();
  const materialCounts = countMap();
  const keywordCounts = countMap();
  const desirePhraseCounts = countMap();

  // Aggregate from pin summaries + structured OpenAI results + notes
for (const pin of pins) {
  const kind = String(pin?.kind || "").toLowerCase();

  // ---- CATALOG (and any non image/note pins) ----
  // These don't live in editFeaturesStore, so read directly from pin/product.
  if (kind !== "image" && kind !== "note") {
    const p = pin?.product || pin?.item || pin?.data || pin || null;

    const garmentType =
      p?.category || p?.garmentType || pin?.category || pin?.garmentType || null;

    const colors =
      asArray(p?.colors || p?.colorFamilies || p?.colorFamily || p?.palette || pin?.colors || pin?.colorFamily);

    const materials =
      asArray(p?.materials || p?.fabrics || p?.material || pin?.materials);

    const keywords =
      asArray(p?.styleTags || p?.keywords || p?.tags || pin?.styleTags);

    if (garmentType) inc(garmentCounts, garmentType, 1);
    incMany(colorCounts, colors, 1);
    incMany(materialCounts, materials, 1);
    incMany(keywordCounts, keywords, 1);

    continue;
  }

  // ---- IMAGE / NOTE ----
  const entry = map?.[pin.id];
  if (!entry?.features) continue;

  const f = entry.features || {};

    // Prefer structured if present (from extractImageFeatures), else use whatever exists.
    const structured = (f.structured && typeof f.structured === "object") ? f.structured : null;

    // ---- Notes: extract desire phrases ----
    const noteText = String(f.text || "").trim();
    if (noteText) {
      const phrases = extractDesirePhrases(noteText);
      for (const p of phrases) inc(desirePhraseCounts, p, 1);
    }

    // ---- Images: count garments/colors/materials/keywords from structured ----
    if (structured) {
      // garmentType/type/category
      const garmentType = structured.garmentType || structured.type || structured.category;
      if (garmentType) inc(garmentCounts, garmentType, 1);

      incMany(colorCounts, asArray(structured.colors || structured.colorFamilies || structured.palette), 1);
      incMany(materialCounts, asArray(structured.materials || structured.fabrics), 1);
      incMany(keywordCounts, asArray(structured.keywords || structured.tags || structured.styleTags), 1);

      continue; // structured is authoritative; no need to parse caption
    }

    // ---- Fallback: if no structured, use summary/caption text ----
    const summary = String(f.summary || summarisePinFeatures(f) || "").toLowerCase();
    if (!summary) continue;

    // lightweight fallback parsing:
    // - garment-ish words: count coarse buckets so UI still shows something
    const coarseGarments = [
      "coat","trench","blazer","jacket","dress","skirt","jeans","trousers","pants",
      "shirt","blouse","tee","tshirt","sweater","knit","cardigan","boots","sneakers",
      "loafers","heels","bag","scarf","belt","cap","sunglasses"
    ];
    for (const g of coarseGarments) {
      if (summary.includes(g)) inc(garmentCounts, g, 1);
    }

    const colors = [
      "black","white","cream","beige","tan","brown","grey","navy","blue","teal",
      "green","olive","yellow","orange","red","burgundy","pink","purple","metallic"
    ];
    for (const c of colors) {
      if (summary.includes(c)) inc(colorCounts, c, 1);
    }
  }

  // derive palette: top 3 colors
  const dominant = Object.entries(colorCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([k]) => k);

  const top = (m, n = 12) =>
    Object.entries(m || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, n)
      .map(([k, v]) => ({ key: k, count: v }));

  const agg = {
    sourceHash: nextHash,

    // Keep existing UI contract
    palette: { dominant_families: dominant },

    // Counts for merchant/user reporting
    garmentCounts,
    colorCounts,
    materialCounts,
    keywordCounts,

    // "Customer desire beyond catalog items"
    // (phrases come from notes; later you can merge in LLM intent extraction too)
desire: {
  topGarments: top(garmentCounts, 12),
  topColors: top(colorCounts, 12),
  topMaterials: top(materialCounts, 12),
  topKeywords: top(keywordCounts, 20),
  topPhrases: top(desirePhraseCounts, 15),
},
  };

  return setEditAggregate(editId, agg);
}